/**
 * EventDirectory.js
 * ─────────────────
 * External event portals + "back to app" CTA.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Sparkles, Search } from 'lucide-react';
import { API } from '../utils/apiConfig';

export default function EventDirectory() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const labelKey = i18n.language === 'de' ? 'label_de' : 'label_en';

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/categories`);
        setCats(r.data?.categories || []);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const clickPortal = async (category, portal) => {
    try {
      axios.post(`${API}/events/click`, {
        category: category.code, portal: portal.name, url: portal.url,
      });
    } catch {}
    window.open(portal.url, '_blank', 'noopener,noreferrer');
  };

  const goOfferRide = () => navigate('/create-ride?source=events');
  const goSearchRide = () => navigate('/search?source=events');

  return (
    <div data-testid="event-directory-page" style={st.page}>
      <div style={st.header}>
        <button data-testid="events-back" onClick={() => navigate(-1)} style={st.backBtn}>
          <ArrowLeft size={16} /> {t('common.back', 'Zurück')}
        </button>
        <h1 style={st.title}>
          <Sparkles size={20} color="#a855f7" /> {t('events.title', 'Events entdecken')}
        </h1>
      </div>

      <p style={st.lead}>
        {t('events.lead',
          'Finde dein Event — dann organisieren wir für dich Hin- und Rückfahrt. Alle Links öffnen in einem neuen Tab.')}
      </p>

      <div style={st.cta}>
        <button data-testid="events-offer-ride" onClick={goOfferRide} style={st.ctaBtn}>
          🚗 {t('events.offerRide', 'Fahrt anbieten')}
        </button>
        <button data-testid="events-search-ride" onClick={goSearchRide} style={st.ctaBtnAlt}>
          <Search size={16} /> {t('events.searchRide', 'Mitfahrt finden')}
        </button>
      </div>

      {loading && <div style={st.empty}>…</div>}

      <div style={st.grid}>
        {cats.map(c => (
          <div key={c.code} data-testid={`event-cat-${c.code}`}
               style={{ ...st.card, borderTop: `4px solid ${c.accent}` }}>
            <div style={st.cardHead}>
              <span style={st.icon}>{c.icon}</span>
              <h3 style={st.cardTitle}>{c[labelKey] || c.label_en}</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {c.portals.map(p => (
                <button
                  key={p.name}
                  data-testid={`event-portal-${c.code}-${p.name.replace(/\s+/g,'-').toLowerCase()}`}
                  onClick={() => clickPortal(c, p)}
                  style={st.portalBtn}
                >
                  <span>{p.name}</span>
                  <ExternalLink size={14} color="#94a3b8" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg,#0b1220,#020617)', padding: '24px 16px', color: '#f1f5f9' },
  header: { maxWidth: 960, margin: '0 auto 14px', display: 'flex', alignItems: 'center', gap: 12 },
  backBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 10px', color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  title: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 22, margin: 0 },
  lead: { maxWidth: 960, margin: '0 auto 18px', color: '#94a3b8', fontSize: 14, lineHeight: 1.5 },
  cta: { maxWidth: 960, margin: '0 auto 22px', display: 'flex', gap: 10, flexWrap: 'wrap' },
  ctaBtn: { padding: '12px 18px', borderRadius: 12, background: 'linear-gradient(135deg,#a855f7,#6366f1)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  ctaBtnAlt: { padding: '12px 18px', borderRadius: 12, background: 'rgba(255,255,255,0.06)', color: '#f1f5f9', border: '1px solid rgba(255,255,255,0.12)', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 },
  grid: { maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 16 },
  card: { padding: 16, borderRadius: 14, background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(255,255,255,0.07)' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  cardTitle: { margin: 0, fontSize: 16, fontWeight: 700 },
  icon: { fontSize: 26 },
  portalBtn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', cursor: 'pointer', fontSize: 14, textAlign: 'left' },
  empty: { textAlign: 'center', color: '#64748b', padding: 30 },
};
